import React from 'react';
import { useNavigate } from 'react-router-dom';
import CollapsibleSection from './CollapsibleSection';

// Icons
const ClockIcon = () => (
  <svg className="section-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);

export interface UpcomingShipment {
  id: string;
  reference: string;
  contractRef?: string;
  supplier: string;
  buyer: string;
  pol?: string;
  pod?: string;
  etd?: string;
  eta?: string;
  cyCut?: string | null;
  cyCutRemaining?: string | null;
  portCutoff?: string; // legacy
  carrier?: string | null;
  vessel?: string | null;
  voyage?: string | null;
  bookingNumber?: string | null;
  status?: string;
  daysUntilCutoff?: number; // legacy
  category?: '30days' | '60days' | '90days'; // legacy
}

interface UpcomingShipmentsSectionProps {
  shipments: UpcomingShipment[];
  onSendSI?: (shipment: UpcomingShipment) => void;
  onViewEmail?: (shipment: UpcomingShipment) => void;
}

const UpcomingShipmentsSection: React.FC<UpcomingShipmentsSectionProps> = ({
  shipments,
  onSendSI,
  onViewEmail,
}) => {
  const navigate = useNavigate();

  // Helper to get cutoff display
  const getCutoffDisplay = (s: UpcomingShipment) => s.cyCut || s.portCutoff || '-';
  const getEtaDisplay = (s: UpcomingShipment) => s.eta || '-';

  const handleReferenceClick = (reference: string) => {
    navigate(`/contracts/${reference}`);
  };

  return (
    <CollapsibleSection
      title="Upcoming Shipments"
      count={shipments.length}
      icon={<ClockIcon />}
    >
      {shipments.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">📅</div>
          <div className="empty-state-text">No upcoming shipments</div>
        </div>
      ) : (
        <div className="table-wrapper">
          <table className="dash-table">
            <thead>
              <tr>
                <th>Reference</th>
                <th>Supplier</th>
                <th>Buyer</th>
                <th className="sortable">CY Cut</th>
                <th>ETA</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {shipments.map((shipment) => (
                <tr 
                  key={shipment.id}
                  className={shipment.cyCutRemaining?.includes('h') ? 'priority-urgent' : 'priority-normal'}
                >
                  <td>
                    <span 
                      className="ref-link"
                      onClick={() => handleReferenceClick(shipment.reference)}
                    >
                      {shipment.reference}
                    </span>
                  </td>
                  <td>{shipment.supplier}</td>
                  <td>{shipment.buyer}</td>
                  <td>{getCutoffDisplay(shipment)}</td>
                  <td>{getEtaDisplay(shipment)}</td>
                  <td>
                    <div className="action-links">
                      <button 
                        className="dash-btn dash-btn-primary dash-btn-sm"
                        onClick={() => onSendSI?.(shipment)}
                      >
                        Send SI
                      </button>
                      <button 
                        className="dash-btn dash-btn-outline dash-btn-sm"
                        onClick={() => onViewEmail?.(shipment)}
                      >
                        View Email
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </CollapsibleSection>
  );
};

export default UpcomingShipmentsSection;
